/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {isNil, isString} from 'lodash';
import {isEmpty} from './StringUtil';

/**
 * Provides the default email address regex used by the isValidEmail
 * and emailValidator utilities.
 */
const emailRegex = new RegExp(
  '^(?:[a-z0-9#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-z0-9#$%&\'*+/=?^_`{|}~-]+)*)@' +
  '(?:' +
    '(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+' + // domain/subdomain
    '[a-z0-9](?:[a-z0-9-]*[a-z0-9]){1,24}' +
    '|' +
    '\\[' +
      '(?:' +
        '(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}' + // IPv4
        '(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?' +
        '|' + // OR IPv6
        '[a-z0-9-]*[a-z0-9]:' +
          '(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f' +
          '\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+' +
      ')' +
    '\\]' +
  ')$',
  'i'
);

/**
 * Provides the default positive number(integer,decimal, float, etc...) except zero with
 * or without prefix '+' regex used by the isPostiveNumberExcludeZero.
 */
const positiveNumberExcludeZeroRegex = /^[+]?(?!0*[.,]0*$|[.,]0*$|0*$)\d+[,.]?\d{0,9}$/;

/**
 * Provides the default positive number(integer,decimal, float, etc...) including zero with
 * or without prefix '+' regex used by the isPostiveNumber.
 */
const positiveNumberRegex = /^[+]?([0-9]+(?:[\.][0-9]*)?|\.[0-9]+)$/;

// eslint-disable-next-line max-len
const urlRegex = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

const stringigyRegex = /[{}\[\]\/]/;

const usZipCodeValidator = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

const usPhoneNumberValidator = /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

// ISO date-time regex, will verify true with or without milliseconds.
const isoDateTimeValidator = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]{3})?Z$/;

// API Search Query String Regex (e.g '(id: *)')
const defaultQueryStringRegex = /\(+id\s?:\s?\(?\*\s?\)+/;

/**
 * Provides the default special character used by the isValidSpecialCharacter utilities.
 */
const specialCharacterRegex = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

/**
 * @isValidEmail
 *
 * The isValidEmail validator returns true if the provided email address is valid,
 * otherwise, returns false.
 *
 * ```typescript
 *
 *    import {isValidEmail} from '@ion/core/src/utils/Validators';
 *
 *    isValidEmail('efeminella@.com') // false
 *    isValidEmail('@calamp.com') // false
 *    isValidEmail('efeminella@calamp.com') // true
 *
 * ```
 *
 * @param   {string} val The email address to be validated
 * @returns {boolean} True if the email address is valid, otherwise, false.
 */
export const isValidEmail = (value: string): boolean => {
  return !(isEmpty(value) || !emailRegex.test(value));
};

/*
 * Provide a utility which determines if the given value is a valid CSV list of email
 * addressed.
 *
 * ```typescript
 *
 *    import {isCommaSeparatedEmail} from '@ion/core/src/utils/';
 *
 *    isCommaSeparatedEmail('efeminella@.com,' ',efeminella,) // false
 *    isCommaSeparatedEmail('@calamp.com','#','*',efeminella@ccc.com) // false
 *    isCommaSeparatedEmail('efeminella@calamp.com,validemail@calamp.com,mark@calamp.com') // true
 *
 * ```
 *
 * @param   {string} val The email address to be validated
 * @returns {boolean} True if the email address is valid, otherwise, false.
 */
export const isCommaSeparatedEmail = (value: string): boolean => {
  return isValidCommaSeparatedValue(value, isValidEmail);
};

/**
 * The function isCommaSeparatedSecondaryEmail will validate the email if input provide
 * else return true
 *
 * ```typescript
 *
 *    import {isCommaSeparatedSecondaryEmail} from '@ion/core/src/utils/';
 *
 *    isCommaSeparatedSecondaryEmail('') // true
 *    isCommaSeparatedSecondaryEmail('efeminella@.com,' ',efeminella,) // false
 *    isCommaSeparatedSecondaryEmail('@calamp.com','#','*',efeminella@ccc.com) // false
 *    isCommaSeparatedSecondaryEmail('efeminella@calamp.com,validemail@calamp.com') // true
 *
 * ```
 *
 * @param   {string} val The email address to be validated
 * @returns {boolean} True if the email address is valid or empty, otherwise, false.
 */
export const isCommaSeparatedSecondaryEmail = (value: string): boolean => {
  if (value && !isEmpty(value)) {
    return isCommaSeparatedEmail(value);
  }
  return true;
};

/**
 * The isNegativeOrDecimalNumber validator returns true if the provided value is less than or
 * equal to zero, or is a decimal. Otherwise returns false.
 *
 * ```typescript
 *
 *    import {isNegativeOrDecimalNumber} from '@ion/core/src/utils/';
 *
 *    isNegativeOrDecimalNumber('0') // true
 *    isNegativeOrDecimalNumber('-1') // true
 *    isNegativeOrDecimalNumber('1') // false
 *
 * ```
 *
 * @param   {string | number} val The number or string to be validated.
 * @returns {boolean} True if val is invalid, otherwise false.
 */
export const isNegativeOrDecimalNumber = (val): boolean => {
  if (!isNil(val)) {
    const value = isString(val) ? parseInt(val, 10) : val;
    return Math.sign(value) !== 1 || String(val).includes('.');
  }
  return false;
};

/**
 * The isPostiveNumberExcludeZero validator returns false if the provided value is less than or
 * equal to zero, Otherwise returns true.
 *
 * ```typescript
 *
 *    import {isPostiveNumberExcludeZero} from '@ion/core/src/utils/';
 *
 *    isPostiveNumberExcludeZero('0') // false
 *    isPostiveNumberExcludeZero('-1') // false
 *    isPostiveNumberExcludeZero('1') // true
 *    isPostiveNumberExcludeZero('+1') // true
 *    isPostiveNumberExcludeZero('1.12') // true
 *    isPostiveNumberExcludeZero('-1.12') // false
 *
 * ```
 *
 * @param   {string | number} val The number or string to be validated.
 * @returns {boolean} True if val is valid, otherwise false.
 */
export const isPostiveNumberExcludeZero = (value): boolean => {
  return positiveNumberExcludeZeroRegex.test(value);
};

/**
 * The isPostiveNumber validator returns false if the provided value is less than zero.
 * Otherwise returns true.
 *
 * ```typescript
 *
 *    import {isPostiveNumber} from '@ion/core/src/utils/';
 *
 *    isPostiveNumber('0') // trues
 *    isPostiveNumber('-1') // false
 *    isPostiveNumber('1') // true
 *    isPostiveNumber('+1') // true
 *    isPostiveNumber('1.12') // true
 *    isPostiveNumber('-1.12') // false
 *
 * ```
 *
 * @param   {string | number} val The number or string to be validated.
 * @returns {boolean} True if val is valid, otherwise false.
 */
export const isPostiveNumber = (value): boolean => {
  return positiveNumberRegex.test(value);
};

/**
 * The isPositiveInteger validator returns false if the provided value
 * is not a whole, positive (zero included), number.
 *
 * ```typescript
 *
 *    import {isPositiveInteger} from '@ion/core/src/utils/';
 *
 *    isPositiveInteger('0')     // true
 *    isPositiveInteger('-1')    // false
 *    isPositiveInteger('1')     // true
 *    isPositiveInteger('1.12')  // false
 *    isPositiveInteger('-1.12') // false
 *    isPositiveInteger('.2')    // false
 *    isPositiveInteger('2.')    // false
 *
 * ```
 */
export const isPositiveInteger = (value: string): boolean => {
  if (value && !value.endsWith('.')) {
    const val = Number(value);
    if (val >= 0) {
      return Number.isInteger(val);
    }
  }
  return false;
};

/**
 * The isPhoneNumber validator returns true if the provided value has the following inputs
 * Number, Plus Symbol, open and close bracket, hyphen otherwise, returns false.
 */
const phoneRegex = /^[+][0-9]{10,14}$/;

/*
 * Provide a utility which determines if the given value is a valid phone number.
 *
 * ```typescript
 *
 *    import {isPhoneNumber} from '@ion/core/src/utils/';
 *
 *    isPhoneNumber('#(123) - 456-7890') // false
 *    isPhoneNumber('+(abc) - 456-7890') // false
 *    isPhoneNumber('(123) 456-7890') // false
 *    isPhoneNumber('+1234567890') // true
 *    isPhoneNumber('+12345678901') // true
 *
 * ```
 *
 * @param   {string} val The email address to be validated
 * @returns {boolean} True if the email address is valid, otherwise, false.
 */
export const isPhoneNumber = (value: string): boolean => {
  return !(isEmpty(value) || !phoneRegex.test(value));
};

/*
 * Provide a utility which determines if the given value is a valid list of CVS formatted
 * phone numbers.
 *
 * ```typescript
 *    import {isCommaSeparatedPhone} from '@ion/core/src/utils/';
 *
 *    isCommaSeparatedPhone('efeminella@.com,' ',efeminella,) // false
 *    isCommaSeparatedPhone('@calamp.com','#','*',efeminella@ccc.com) // false
 *    isCommaSeparatedPhone('efeminella@calamp.com,validemail@calamp.com,mark@calamp.com') // true
 *
 * ```
 *
 * @param   {string} val The email address to be validated
 * @returns {boolean} True if the email address is valid, otherwise, false.
 */
export const isCommaSeparatedPhone = (value: string): boolean => {
  return isValidCommaSeparatedValue(value, isPhoneNumber);
};

/**
 * The isGreaterThan validator returns true if the provided value is less than
 * zero or greater than the number to be compared, otherwise returns false.
 *
 * ```typescript
 *
 *    import {isGreaterThan} from '@ion/core/src/utils/';
 *
 *    isGreaterThan('-2', '-2') // true
 *    isGreaterThan('3', '2') // true
 *    isGreaterThan('2', '3') // false
 *    isGreaterThan('2', '2') // false
 *    isGreaterThan('1', '0') // false
 *
 * ```
 *
 * @param   {string | number} val The number or string to be validated.
 * @param   {string | number} compareVal The number or string to be compared with.
 * @returns {boolean} True if val is invalid, otherwise false
 */
export const isGreaterThan = (val, compareVal) => {
  if (!isNil(val) && !isNil(compareVal)) {
    const value  = isString(val) ? parseInt(val) : val;
    const maxVal = isString(compareVal) ? parseInt(compareVal) : compareVal;
    return value < 0 || value > maxVal;
  }
  return false;
};

/*
* ```typescript
*
*    import {isValidCommaSeparatedValue} from '@ion/core/src/security';
*
*    isValidCommaSeparatedValue('efeminella@gmail.com,efeminella@altran.com', isValidEmail) // true
*    isValidCommaSeparatedValue('+216756787765,+217877675642', isPhoneNumber) // true
*    isValidCommaSeparatedValue('efeminella@gmail.com,efeminella@altran.com', 'isValidEmail') // false
*
* ```
*
* @param   {string} val The list of comma separated values to be validated
* @param   {FunctionStringCallback} Callback function to validate each value against its regex
* @returns {boolean} True if all values are valid, otherwise, false.
*/
export const isValidCommaSeparatedValue = (value: string, validator: FunctionStringCallback): boolean => {
  const result = value.split(/,/);
  return result.every(validator);
};

/**
 * The isValidURL validator returns true if the provided url is valid,
 * otherwise, returns false.
 *
 * ```typescript
 *
 *    import {isValidURL} from '@ion/core/src/utils/';
 *
 *    isValidURL('abc@.com') // false
 *    isValidURL('www.calamp') // false
 *    isValidURL('http://calamp.com') // true
 *
 * ```
 *
 * @param   {string} val The URL to be validated
 * @returns {boolean} True if the url is valid, otherwise, false.
 */
export const isValidURL = (value: string): boolean => {
  return !(isEmpty(value) || !urlRegex.test(value));
};

/**
 * The isStringifyValue validator returns true if the provided Value is valid,
 * otherwise, returns false.
 *
 * ```typescript
 *
 *    import {isStringifyValue} from '@ion/core/src/utils/';
 *
 *    isStringifyValue('abc@.com') // false
 *    isStringifyValue('test, test') // false
 *    isStringifyValue(''[{\"id\":\"286691\",\"name\":\"AutomationiOnTool3\",\"type\":\"Powered\",
 *    \"status\":\"Enabled\"}]') // true
 *
 * ```
 *
 * @param   {string} val The value to be validated
 * @returns {boolean} True if the value is valid, otherwise, false.
 */
export const isStringifyValue = (value: string): boolean => {
  return stringigyRegex.test(value);
};

/**
 * The isValidUSZip validator returns true if the provided url is valid,
 * otherwise, returns false.
 *
 * ```typescript
 *
 *    import {isValidUSZip} from '@ion/core/src/utils/';
 *
 *    isValidUSZip('abc@.com') // false
 *    isValidUSZip('www12') // false
 *    isValidUSZip('12345') // true
 *
 * ```
 *
 * @param   {string} val The ZIP to be validated
 * @returns {boolean} True if the ZIP is valid, otherwise, false.
 */
export const isValidUSZip = (value: string): boolean => {
  return !(isEmpty(value) || !usZipCodeValidator.test(value));
};

/**
 * The isValidUSAddress validator returns true if the provided zip and
 * country is belongs to US is valid, otherwise, returns false.
 *
 * @param   {string} val The ZIP to be validated
 * @returns {string} val The countryCd to be validated.
 */
export const isValidUSAddress = (country: string, zip: string): boolean => {
  return country === 'US' && isValidUSZip(zip);
};

/**
 * The isValidUSPhoneNumber  validator returns true if the provided value is valid,
 * otherwise, returns false.
 *
 * ```typescript
 *
 *    import {isValidUSPhoneNumber } from '@ion/core/src/utils/';
 *
 *   isValidUSPhoneNumber('1234567890')     // true
 *   isValidUSPhoneNumber(1234567890)       // true
 *   isValidUSPhoneNumber('(078)789-8908')  // true
 *   isValidUSPhoneNumber('123-345-3456')   // true
 *   isValidUSPhoneNumber(123456)           // false
 *   isValidUSPhoneNumber('abcdedjffff')    // false
 *   isValidUSPhoneNumber('www.abcd.com')   // false
 *
 * ```
 *
 * @param   {string} val The Phone Number to be validated
 * @returns {boolean} True if the Phone Number is valid, otherwise, false.
 */
export const isValidUSPhoneNumber  = (value: string): boolean => {
  return !(isEmpty(value) || !usPhoneNumberValidator.test(value));
};

/**
 * The isValidExtensionNumber validator determines if the provided value is less than,
 * or equal to the given limit (which defaults to six), returning true if so, otherwise,
 * false.
 *
 * ```typescript
 *
 *    import { isValidExtensionNumber } from '@ion/core/src/utils/';
 *
 *   isValidExtensionNumber('324521')   //true
 *   isValidExtensionNumber(478392)     //true
 *   isValidExtensionNumber(123-34)     //false
 *   isValidExtensionNumber(123abc)     //false
 *   isValidExtensionNumber(abcde)      //false
 *
 * ```
 *
 * @param {string} val The Extension Number to be validated
 * @returns {boolean} True if the Extension Number is lesser or equal to six, otherwise false.
 */
export const isValidExtensionNumber = (value: string, limit = 6): boolean => {
  return value?.length > limit;
};

/**
 * Checks a given value and returns whether or not it matches the format of an ISO 8601 date-time string.
 *
 * ```typescript
 *  import {isValidISODateTime} from '@ion/core/src/utils/';
 *
 *  isValidISODateTime('2022-09-15T14:08:00.000Z'); // true
 *  isValidISODateTime('2022-09-14T04:07:34.495Z'); // true
 *  isValidISODateTime('2022-09-14T04:07:34Z');     // true (without milliseconds is valid as well)
 *
 *  isValidISODateTime('');                         // false
 *  isValidISODateTime('3');                        // false
 *  isValidISODateTime('32022-09-14T04:07:34Z');    // false (extra starting digit)
 *  isValidISODateTime('12/34/56 8:49PM');          // false
 *
 * ```
 * @param value The potential date-time string
 * @returns true if the given value is an ISO date-time, false otherwise
 */
export const isValidISODateTime = (value: string): boolean => {
  return !!(!isEmpty(value) && isoDateTimeValidator.test(value));
};

/**
 * Checks a given value and returns whether or not it is valid percentage value.
 *
 * ```typescript
 *  import {isValidPercentage} from '@ion/core/src/utils/Validators';
 *
 *  isValidPercentage(-1)  // false
 *  isValidPercentage(0)   // true
 *  isValidPercentage(1)   // true
 *  isValidPercentage(100) // true
 *  isValidPercentage(101) // false
 *
 * ```
 * @param value The percentage value
 * @returns true if the given percentage value is valid, false otherwise
 */
export const isValidPercentage = (value: number) => (value >= 0 && value <= 100);

/**
 * Determines if the given query string is the default query string.
 * (e.g 'id: (*)' or '(id: *)' or '((id: *))', etc).
 *
 * @param queryString the query string to test
 * @returns true if the given query string is a form of the default query string, false otherwise
 */
export const isDefaultQueryString = (queryString: string) => defaultQueryStringRegex.test(queryString);

/**
 * The isValidSpecialCharacter validator returns true if the provided string is valid,
 * otherwise, returns false.
 *
 * ```typescript
 *
 *    import {isValidSpecialCharacter} from '@ion/core/src/utils/';
 *
 *    isValidSpecialCharacter('"465830","1302Test"') // true
 *    isValidSpecialCharacter('"Connect Telematics","Enabled"') // true
 *    isValidSpecialCharacter('2312005643') // false
 *
 * ```
 *
 * @param   {string} val The special character to be validated
 * @returns {boolean} True if the special character is valid, otherwise, false.
 */
export const isValidSpecialCharacter = (value: string): boolean => {
  return specialCharacterRegex.test(value);
};

/**
 * Check the given Domain name is valid or not and return the boolean value 
 * accordingly.
 * 
 * ```typescript
 *   import {isValidDomain} from '@ion/core/src/utils/Validators';
 *   ...
 *   
 *    isValidDomain('calamp.com') // true
 *    isValidDomain('verizon.com) // true
 *    isValidDomain('verizon') // false
 *    isValidDomain('https://calamp.om') // true
 * 
 * ```
 * 
 *  @param   domain The given string should be validated
 *  @returns `true` if the domain is valid, otherwise, `false`.
 */
export const isValidDomain = (domain: string): boolean => {
  const domainPattern = /^(?!-)([A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}$/;
  domain = domain?.indexOf('http') >= 0 ? domain?.split('//')?.[1] : domain;
  return domainPattern.test(domain);
};
